import React, { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  IconButton,
  Table as MUITable,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
// import { PsychologyReverse } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { Menu } from "components/popups";
import { TableSkeletonLoader } from "components/Table-skeleton-loader/TableSkeletonLoader";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useDevice } from "hooks";
import { useDealActions } from "store";
import {
  IImportCsvResult,
  IInvestmentDetailCellValue,
  IInvestmentDetailColumn,
  IInvestmentDetailField,
  IInvestmentDetailRow,
  IInvestmentDetailTableData
} from "store/types";
import pickFiles from "utils/pick-files";
import { toastify } from "utils/tyostify";

import { currencySign, RoleName } from "../../constants";
import { EditCellPopover } from "./Edit-cell-popover/EditCellPopover";
import { ImportCsvResultModal } from "./Import-csv-result-modal/ImportCsvResultModal";
import { InvestmentDetailActions } from "./InvestmentDetailActions";
import { UpdateLookThroughModal } from "./Update-look-through-modal/UpdateLookThroughModal";

interface IPopoverProps {
  element: HTMLElement | null;
  cellData: IInvestmentDetailCellValue;
  columnData: IInvestmentDetailColumn;
}

interface ITableProps {
  columns: IInvestmentDetailField[];
  setRefetch: () => void;
  dealId: number;
  isFreemium?: boolean;
  data: IInvestmentDetailTableData | undefined;
  loading: boolean;
  setData: React.Dispatch<React.SetStateAction<IInvestmentDetailTableData | undefined>>;
  refetchTableData: () => void;
}

export const Table: React.FC<ITableProps> = ({
  columns,
  setRefetch,
  dealId,
  isFreemium = false,
  data,
  loading,
  setData,
  refetchTableData
}) => {
  if (!dealId) return null;
  const { isMobile } = useDevice();
  const {
    exportInvestmentDetailsTableAction,
    importInvestmentDetailsCsvAction,
    deleteInvestmentDetailAction,
    updateDealCharacteristicsFromInvestmentDetailsAction,
    getDealAndStoreAction
  } = useDealActions();
  const [editCell, setEditCell] = useState<IPopoverProps | null>(null);
  const [updateLookThroughModalOpen, setUpdateLookThroughModalOpen] = useState(false);
  const [importResult, setImportResult] = useState<IImportCsvResult | null>(null);

  // const measures = useSimpleMeasures().map((m) => String(m));
  // const calcFields = useCalculatedSelector();
  // const characteristicsAll: string[] = useCharacteristics().map((char) =>
  //   typeof char === "string" ? char : String(char.value)
  // );

  // const groupedOptions = useMemo(() => {
  //   return [
  //     ...measures.map((item) => ({ key: item, title: item, groupKey: "Measures" })),
  //     ...calcFields.map((item) => ({ key: item, title: item, groupKey: "Calculated fields" })),
  //     ...characteristicsAll.map((item) => ({ key: item, title: item, groupKey: "Characteristics" }))
  //   ];
  // }, [measures, calcFields, characteristicsAll]);

  // const flatOptions = useMemo(() => {
  //   return [
  //     ...measures.map((item) => ({ key: item, title: item })),
  //     ...calcFields.map((item) => ({ key: item, title: item })),
  //     ...characteristicsAll.map((item) => ({ key: item, title: item }))
  //   ];
  // }, [measures, calcFields, characteristicsAll]);

  // const mixedOptions = useMemo(() => {
  //   return [...groupedOptions, ...flatOptions];
  // }, [groupedOptions, flatOptions]);

  const { action: deleteAction } = useActionHook(deleteInvestmentDetailAction, {
    onSuccess: (uuid) => {
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.filter((row) => row[prev.columns[0].key].uuid !== uuid)
        };
      });
      setRefetch();
    }
  });

  const { action: updateDealCharacteristics } = useActionHook(updateDealCharacteristicsFromInvestmentDetailsAction, {
    onSuccess: () => {
      getDealAndStoreAction(dealId);
      toastify({ message: "Deal characteristics updated successfully", type: "success" });
    },
    onReject: (err) => {
      toastify({ message: err.message || "Failed to update deal characteristics", type: "error" });
    }
  });

  const { action: importCsvAction, loading: importCsvLoading } = useActionHook(importInvestmentDetailsCsvAction, {
    onSuccess: (data) => {
      setImportResult(data);
      refetchTableData();
      setRefetch();
    },
    onReject: (err) => {
      toastify({ message: err.message || "CSV import failed", type: "error" });
    }
  });

  const handleImportCsvClick = async () => {
    if (importCsvLoading || !dealId) return;
    const fileList = await pickFiles({ accept: ".csv" });
    const file = fileList?.[0];
    if (file) importCsvAction(dealId, file);
  };

  const handleCellClick = (
    event: React.MouseEvent<HTMLElement>,
    cellData: IInvestmentDetailCellValue,
    columnData: IInvestmentDetailColumn
  ) => {
    setEditCell({
      element: event.currentTarget,
      cellData,
      columnData
    });
  };

  const updateCellValue = (updatedCell: IInvestmentDetailCellValue) => {
    if (!data) return;
    if (!updatedCell) return;

    setData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        rows: prev.rows.map((row: IInvestmentDetailRow) => {
          for (const key in row) {
            const cell = row[key];
            if (cell.uuid === updatedCell.uuid && cell.key === updatedCell.key) {
              return {
                ...row,
                [key]: { ...cell, ...updatedCell }
              };
            }
          }
          return row;
        })
      };
    });
  };

  const renderCell = (cell: IInvestmentDetailCellValue) => {
    if (!cell.value) return "-";

    const renderValue = () => {
      const value = cell.value || "-";
      const currency = cell.currency ? currencySign[cell.currency as keyof typeof currencySign] : "";
      const postfix = cell.postfix || "";
      const formattedValue = cell.currency ? Number(value).toLocaleString("en-US") : value;
      return cell.postfix === "%" ? `${value}${postfix}` : `${currency}${formattedValue}`;
    };

    return (
      <Typography
        variant="body2"
        sx={{
          "&:hover": {
            textShadow: "0 0 1px"
          }
        }}
      >
        {renderValue()}
      </Typography>
    );
  };

  const handleDeleteRow = (uuid: string) => {
    if (!dealId) return;
    deleteAction(dealId, uuid);
  };

  const handleUpdateLookThrough = (data: Record<string, Record<string, number>>) => {
    if (!dealId) return;
    updateDealCharacteristics(dealId, data);
  };

  return (
    <Stack
      sx={{
        p: "16px",
        flexGrow: 1,
        overflow: "hidden",
        position: "relative",
        borderRadius: "8px",
        backgroundColor: "#ffffff",
        ...(isFreemium && {
          border: "1px solid #dbe8f5"
        }),
        ...(!isFreemium && {
          boxShadow: "0px 1px 2px 0px #12121714, 0px 1px 4px 0px #1212171F"
        })
        // maxHeight: "calc(100vh - 50px)"
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Typography variant="h6" sx={{ color: "#071F35" }}>
            Companies
          </Typography>
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            {!isFreemium && (
              <>
                {isMobile ? (
                  <MaterialTooltip isShow placement="top" title="Update look through for deal">
                    <IconButton
                      sx={{ width: "24px", height: "24px", padding: 0 }}
                      onClick={() => setUpdateLookThroughModalOpen(true)}
                    >
                      <EditIcon sx={{ fontSize: "18px", color: "#071F35" }} />
                    </IconButton>
                  </MaterialTooltip>
                ) : (
                  <Button
                    variant="outlined"
                    sx={{ color: "#071F35", padding: "2px 5px", minHeight: "24px", height: "24px", fontSize: "14px" }}
                    onClick={() => setUpdateLookThroughModalOpen(true)}
                  >
                    Update Look Through for Deal
                  </Button>
                )}
              </>
            )}
          </RoleComponentGuard>
        </Box>
        {!isFreemium && (
          <Stack direction="row" gap="8px" alignItems="center">
            <InvestmentDetailActions
              dealId={dealId}
              columns={columns}
              onChartsRefetch={() => setRefetch()}
              onTableRefetch={refetchTableData}
            />
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <MaterialTooltip isShow placement="top" title="Import CSV">
                <IconButton onClick={handleImportCsvClick} disabled={importCsvLoading}>
                  <UploadFileIcon />
                </IconButton>
              </MaterialTooltip>
              <MaterialTooltip isShow placement="top" title="Export Table Data">
                <IconButton onClick={() => dealId && exportInvestmentDetailsTableAction(dealId)}>
                  <FileDownloadOutlinedIcon />
                </IconButton>
              </MaterialTooltip>
            </RoleComponentGuard>
          </Stack>
        )}
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          flexGrow: 1,
          borderRadius: "8px",
          border: "1px solid #DBE8F5",
          boxShadow: "none",
          maxHeight: isFreemium ? "224px" : "none",
          overflow: "auto"
        }}
      >
        {loading ? (
          <TableSkeletonLoader rowsNum={10} />
        ) : (
          <MUITable stickyHeader sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow>
                {data?.columns.map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{ fontSize: "14px", fontWeight: 500, "&:first-child": { borderRadius: "7px 0 0 0" } }}
                  >
                    {column.title}
                  </TableCell>
                ))}
                {!isFreemium && <TableCell sx={{ width: "40px", borderRadius: "0 7px 0 0" }}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.rows.map((row: IInvestmentDetailRow, index) => (
                <TableRow key={index} sx={{ ":hover": { bgcolor: "#DBE8F5", cursor: "pointer" } }}>
                  {data?.columns.map((column) => (
                    <TableCell
                      key={column.id}
                      onClick={(e) => handleCellClick(e, row[column.key], column)}
                      sx={{
                        borderBottom: "1px solid #dbe8f5",
                        color: "#26A6AE",
                        cursor: "pointer",
                        "&:hover": {
                          textShadow: "0 0 1px"
                        }
                      }}
                    >
                      {renderCell(row[column.key])}
                    </TableCell>
                  ))}
                  {!isFreemium && (
                    <TableCell sx={{ width: "40px", borderBottom: "1px solid #dbe8f5" }}>
                      <Menu
                        options={[
                          {
                            title: "Remove",
                            icon: <DeleteIcon sx={{ fill: "#071F35" }} />,
                            onClick: () => handleDeleteRow(row[data.columns[0].key].uuid)
                          }
                        ]}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </MUITable>
        )}
      </TableContainer>
      {editCell && dealId && (
        <EditCellPopover
          dealId={dealId}
          anchorEl={editCell.element}
          onClose={() => setEditCell(null)}
          cellData={editCell.cellData}
          columnData={editCell.columnData}
          onSubmit={(value) => {
            updateCellValue(value);
            setRefetch(); //if user changed or added new data, we need to refetch charts
          }}
        />
      )}
      {/* <TableColumnsMenu
        triggerContainerSx={{ position: "absolute", top: "10px", right: 0, zIndex: 10 }}
        state={[]}
        options={groupedOptions}
        onSubmit={(state) => console.log(state)}
        menuTitle="Grouped columns"
      />
      <TableColumnsMenu
        triggerContainerSx={{ position: "absolute", top: "10px", right: "40px", zIndex: 10 }}
        state={[]}
        options={flatOptions}
        onSubmit={(state) => console.log(state)}
        menuTitle="Flat columns"
      />
      <TableColumnsMenu
        triggerContainerSx={{ position: "absolute", top: "10px", right: "80px", zIndex: 10 }}
        state={[]}
        options={mixedOptions}
        onSubmit={(state) => console.log(state)}
        menuTitle="Mixed columns"
      /> */}
      <UpdateLookThroughModal
        open={updateLookThroughModalOpen}
        onClose={() => setUpdateLookThroughModalOpen(false)}
        onSubmit={handleUpdateLookThrough}
        columns={columns}
        tableData={data || null}
      />
      <ImportCsvResultModal onClose={() => setImportResult(null)} result={importResult} />
    </Stack>
  );
};
